import type { Chart as ChartJS, TooltipModel } from 'chart.js';
import { clampFixedTooltipPosition } from '@/domains/pages/shared/chart-tooltip';

type TooltipOffset = {
  x: number;
  y: number;
};

export { clampFixedTooltipPosition };

export function ensureChartTooltipHost(chart: ChartJS): HTMLElement {
  const canvas = chart.canvas;
  const parent = canvas.parentNode as HTMLElement | null;
  if (!parent) {
    throw new Error('Chart canvas has no parent element');
  }

  if (getComputedStyle(parent).position === 'static') {
    parent.style.position = 'relative';
  }

  return parent;
}

export function positionChartExternalTooltip(
  chart: ChartJS,
  tooltipEl: HTMLElement,
  tooltipModel: Pick<TooltipModel<'line'>, 'caretX' | 'caretY'>,
  offset: TooltipOffset = { x: 12, y: -12 },
): void {
  if (!tooltipEl.parentNode) {
    document.body.appendChild(tooltipEl);
  }

  tooltipEl.style.background = 'transparent';
  tooltipEl.style.border = 'none';
  tooltipEl.style.boxShadow = 'none';
  tooltipEl.style.padding = '0';
  tooltipEl.style.margin = '0';
  tooltipEl.style.opacity = '1';
  tooltipEl.style.position = 'fixed';
  tooltipEl.style.pointerEvents = 'none';
  tooltipEl.style.zIndex = '9999';

  const canvas = chart.canvas;
  const rect = canvas.getBoundingClientRect();

  let left = rect.left + tooltipModel.caretX + offset.x;
  let top = rect.top + tooltipModel.caretY + offset.y;

  const tooltipWidth = tooltipEl.offsetWidth;
  const tooltipHeight = tooltipEl.offsetHeight;

  const VIEWPORT_PADDING = 8;

  if (tooltipHeight > 0 && top + tooltipHeight > window.innerHeight - VIEWPORT_PADDING) {
    top = rect.top + tooltipModel.caretY - tooltipHeight - Math.abs(offset.y);
  }
  if (tooltipHeight > 0 && top < VIEWPORT_PADDING) {
    top = rect.top + tooltipModel.caretY + 16;
  }
  if (tooltipWidth > 0 && left + tooltipWidth > window.innerWidth - VIEWPORT_PADDING) {
    left = rect.left + tooltipModel.caretX - tooltipWidth - Math.abs(offset.x);
  }

  const clamped = clampFixedTooltipPosition(left, top, tooltipWidth, tooltipHeight);
  tooltipEl.style.left = `${clamped.left}px`;
  tooltipEl.style.top = `${clamped.top}px`;
}
